package vegasful.admin.views

import androidx.compose.runtime.*
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import vegasful.admin.Theme
import vegasful.admin.components.*

/**
 * The pages an operator read the hours from.
 *
 * These fields are written by hand in v1 and read by nothing yet — they exist so that data is being
 * collected from the first record entered, ready for the Phase 2 hours-verification sweep described
 * in `vegasful-server/specs/things-to-do.md`. That is the whole reason they shipped in v1, so the
 * editor carries them even though no code consumes them.
 *
 * Unlike `hours`, the server does **not** parse this: `applyInput` stores the string verbatim. So
 * this editor is deliberately built on [JsonObject] rather than a typed model and **preserves every
 * key it does not understand**. That matters twice over — the Phase 2 machine fields
 * (`lastChecked`, `lastChanged`, `contentHash`, `status`, `failureCount`) will be written by the
 * sweep and must survive a human edit, and this admin app has a history of unknown JSON fields
 * silently blanking an editor.
 */
@OptIn(kotlinx.serialization.ExperimentalSerializationApi::class)
private val sourcesJson = Json {
    prettyPrint = true
    prettyPrintIndent = "  "
}

/** The fetch strategies from the spec. A page that is an Angular shell needs more than a plain GET. */
private val renderStrategies = listOf("NONE", "JS", "STEALTH")

/** The fields this editor owns. Everything else in an entry is preserved untouched. */
private val humanFields = setOf("url", "render", "selector", "notes")

private fun parseSources(stored: String?): List<JsonObject>? {
    val text = stored?.takeIf { it.isNotBlank() } ?: return emptyList()
    val element = runCatching { sourcesJson.parseToJsonElement(text) }.getOrNull() ?: return null
    return when (element) {
        // A bare object is tolerated as a single source: the field is hand-written, and rejecting a
        // near-miss would leave the operator no way to fix it.
        is JsonObject -> listOf(element)
        is JsonArray -> element.map { it as? JsonObject ?: return null }
        else -> null
    }
}

private fun JsonObject.string(key: String): String? =
    this[key]?.takeIf { it !is JsonNull }?.jsonPrimitive?.contentOrNull

private fun JsonObject.withField(key: String, value: String?): JsonObject =
    JsonObject(toMutableMap().also { map ->
        if (value.isNullOrBlank()) map.remove(key) else map[key] = JsonPrimitive(value)
    })

private fun encodeSources(sources: List<JsonObject>): String =
    if (sources.isEmpty()) "" else sourcesJson.encodeToString(JsonArray.serializer(), JsonArray(sources))

private fun <T> List<T>.replacedAt(index: Int, value: T): List<T> =
    toMutableList().also { it[index] = value }

private fun <T> List<T>.withoutAt(index: Int): List<T> =
    toMutableList().also { it.removeAt(index) }

/**
 * The hours-sources editor. [save] submits replacement JSON and is expected to throw on rejection.
 */
@Composable
fun hoursSourcesBox(stored: String?, save: suspend (String) -> Unit) {
    val parsed = parseSources(stored)

    // When the stored value cannot be read as a list of objects, fall back to editing it as raw
    // text rather than silently discarding it.
    if (parsed == null) {
        rawSourcesBox(stored, save)
        return
    }

    var sources by remember(stored) { mutableStateOf(parsed) }
    var error by remember(stored) { mutableStateOf<String?>(null) }

    val dirty = sources != parsed

    box({
        title = "Hours Sources"

        action {
            title = "Save Sources"
            primary = true
            showProgressOnAction = true
            enabled = dirty
            action {
                error = null
                try {
                    save(encodeSources(sources))
                } catch (t: Throwable) {
                    error = t.message ?: "The hours sources could not be saved."
                }
            }
        }

        action {
            title = "Revert"
            enabled = dirty
            action {
                sources = parsed
                error = null
            }
        }
    }) {
        Div({
            style {
                fontSize(0.8.cssRem)
                opacity(.7)
                marginBottom(8.px)
            }
        }) {
            Text(
                "Where the hours were read from. Filled in by hand; nothing reads these yet — they " +
                        "feed the Phase 2 hours-verification sweep. Two agreeing sources are what will " +
                        "let a change apply automatically later."
            )
        }

        error?.let {
            Div({
                style {
                    backgroundColor(Theme.messageAlertBackground.value())
                    border(1.px, LineStyle.Solid, Theme.messageAlertBorder.value())
                    padding(8.px)
                    marginBottom(10.px)
                    fontSize(0.85.cssRem)
                }
            }) {
                Text(it)
            }
        }

        if (sources.isEmpty()) {
            Div({ style { fontSize(0.85.cssRem); opacity(.7); marginBottom(8.px) } }) {
                Text("No sources recorded.")
            }
        }

        sources.forEachIndexed { index, source ->
            Div({
                style {
                    border(0.px, LineStyle.Solid, Theme.defaultDivider.value())
                    borderWidth(0.px, 0.px, 0.px, 3.px)
                    padding(8.px, 0.px, 8.px, 12.px)
                    marginBottom(10.px)
                }
            }) {
                dialogField("URL", "The page carrying the hours.") {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            property("gap", "8px")
                        }
                    }) {
                        Div({ style { property("flex", "1") } }) {
                            simpleTextField(source.string("url")) {
                                sources = sources.replacedAt(index, source.withField("url", it))
                            }
                        }
                        source.string("url")?.takeIf { it.isNotBlank() }?.let { url ->
                            A(href = url, { target(ATarget.Blank) }) {
                                Text("Open")
                            }
                        }
                        icon("delete") {
                            action { sources = sources.withoutAt(index) }
                        }
                    }
                }

                sideBySideFields {
                    dialogField("Render", "How the page has to be fetched to contain the hours at all.") {
                        Select({
                            onChange { event ->
                                sources = sources.replacedAt(index, source.withField("render", event.value))
                            }
                        }) {
                            val current = source.string("render")
                            Option("", { if (current.isNullOrBlank()) selected() }) { Text("(unset)") }
                            renderStrategies.forEach { strategy ->
                                Option(strategy, { if (current == strategy) selected() }) {
                                    Text(
                                        when (strategy) {
                                            "NONE" -> "NONE — plain fetch"
                                            "JS" -> "JS — needs a browser"
                                            else -> "STEALTH — bot-protected"
                                        }
                                    )
                                }
                            }
                        }
                    }

                    dialogField("Selector", "Optional CSS selector narrowing the region.") {
                        simpleTextField(source.string("selector")) {
                            sources = sources.replacedAt(index, source.withField("selector", it))
                        }
                    }
                }

                dialogField(
                    "Notes",
                    "Free-form prose — \"hours are in the calendar widget, October tab\". The eventual reader is a model."
                ) {
                    TextArea {
                        style { height(56.px) }
                        value(source.string("notes").orEmpty())
                        onInput {
                            sources = sources.replacedAt(index, source.withField("notes", it.value))
                        }
                    }
                }

                machineFields(source)
            }
        }

        Div({ style { marginTop(4.px) } }) {
            button("Add a source") {
                sources = sources + JsonObject(emptyMap())
            }
        }
    }
}

/**
 * The Phase 2 fields, shown read-only when present. They are written by the sweep, not by hand, but
 * seeing them is how an operator understands why a record is flagged.
 */
@Composable
private fun machineFields(source: JsonObject) {
    val machine = source.keys.filterNot { it in humanFields }
    if (machine.isEmpty()) return

    Div({
        style {
            marginTop(6.px)
            fontSize(0.78.cssRem)
            opacity(.75)
        }
    }) {
        Text("Recorded by the verification sweep (read-only, preserved on save): ")
        Text(machine.joinToString(", ") { key ->
            val value = source[key]?.let { element ->
                if (element is JsonPrimitive) element.contentOrNull ?: element.toString() else element.toString()
            }
            "$key=$value"
        })
    }
}

/**
 * The fallback for a stored value this editor cannot model. Editing it as text is strictly better
 * than showing an empty structured editor over content that is still on the record.
 */
@Composable
private fun rawSourcesBox(stored: String?, save: suspend (String) -> Unit) {
    var text by remember(stored) { mutableStateOf(stored.orEmpty()) }
    var error by remember(stored) { mutableStateOf<String?>(null) }

    box({
        title = "Hours Sources"

        action {
            title = "Save Sources"
            primary = true
            showProgressOnAction = true
            enabled = text != stored.orEmpty()
            action {
                error = null
                try {
                    save(text)
                } catch (t: Throwable) {
                    error = t.message ?: "The hours sources could not be saved."
                }
            }
        }
    }) {
        Div({
            style {
                backgroundColor(Theme.messageAlertBackground.value())
                border(1.px, LineStyle.Solid, Theme.messageAlertBorder.value())
                padding(8.px)
                marginBottom(10.px)
                fontSize(0.85.cssRem)
            }
        }) {
            Text(
                "The stored hours sources are not a JSON list of objects, so they are shown as raw " +
                        "text. Replace them with a list — for example " +
                        "[{\"url\": \"https://…\", \"render\": \"JS\"}] — to get the structured editor back."
            )
        }

        error?.let {
            Div({ style { marginBottom(8.px); fontSize(0.85.cssRem) } }) { Text(it) }
        }

        TextArea {
            style { height(140.px) }
            value(text)
            onInput { text = it.value }
        }
    }
}
